<script setup lang="ts">
import { computed } from 'vue'
import JIcon from '@/components/atoms/JIcon.vue'
import JLink from '@/components/atoms/JLink.vue'
import { cn } from '@/lib/utils'

/**
 * JBreadcrumb - 브레드크럼 네비게이션 컴포넌트 (molecules)
 * Breadcrumb Navigation Component
 * 
 * @description
 * 페이지의 네비게이션 경로를 표시하는 브레드크럼 컴포넌트입니다.
 * 
 * Features:
 * - 경로 아이템 표시
 * - 클릭 가능한 링크 지원
 * - 아이콘 지원
 * - 커스터마이징 가능한 구분자
 * 
 * @example
 * ```vue
 * <JBreadcrumb 
 *   :items="[
 *     { label: '홈', href: '/' },
 *     { label: '제품', href: '/products' },
 *     { label: '상세' }
 *   ]"
 * />
 * ```
 */

export type BreadcrumbItem = {
  /** 라벨 텍스트 */
  label: string
  /** 링크 URL (없으면 텍스트만 표시) */
  href?: string
  /** 아이콘 이름 */
  icon?: string
  /** 클릭 핸들러 */
  onClick?: () => void
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'minimal'   // 최소 스타일 (작은 크기, 얇은 구분자)

const props = withDefaults(
  defineProps<{
    /** 브레드크럼 아이템 목록 */
    items: BreadcrumbItem[]
    /** 구분자 (기본값: /) */
    separator?: string
    /** 스타일 타입 */
    styletype?: StyleType
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    separator: '/',
    styletype: 'default',
  }
)

const emit = defineEmits<{
  /** 아이템 클릭 이벤트 */
  itemClick: [item: BreadcrumbItem, index: number]
}>()

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  itemClass: string
  separatorClass: string
  lastItemClass: string
}> = {
  default: {
    containerClass: 'flex items-center gap-2 px-4 py-1.5',
    itemClass: 'text-xs text-muted-foreground hover:text-foreground transition-colors',
    separatorClass: 'text-muted-foreground/50 text-xs',
    lastItemClass: 'text-foreground font-medium',
  },
  minimal: {
    containerClass: 'flex items-center gap-1.5 px-2 py-1',
    itemClass: 'text-xs text-muted-foreground hover:text-foreground transition-colors',
    separatorClass: 'text-muted-foreground/40 text-xs',
    lastItemClass: 'text-foreground font-medium',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 아이템 클릭 핸들러
 */
const handleItemClick = (item: BreadcrumbItem, index: number) => {
  item.onClick?.()
  emit('itemClick', item, index)
}

/**
 * 마지막 아이템인지 확인
 */
const isLastItem = (index: number) => {
  return index === props.items.length - 1
}
</script>

<template>
  <nav 
    :class="cn(preset.containerClass, props.class)"
    aria-label="브레드크럼 네비게이션"
  >
    <ol class="flex items-center gap-2 list-none p-0 m-0">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="flex items-center gap-2"
      >
        <!-- 아이템 -->
        <span
          v-if="isLastItem(index) || (!item.href && !item.onClick)"
          :class="cn(preset.itemClass, preset.lastItemClass, isLastItem(index) && 'cursor-default')"
        >
          <!-- 아이콘 -->
          <JIcon
            v-if="item.icon"
            :name="item.icon"
            size="sm"
            class="mr-1"
          />
          {{ item.label }}
        </span>
        
        <JLink
          v-else
          :href="item.href"
          :class="cn(preset.itemClass)"
          @click="handleItemClick(item, index)"
        >
          <!-- 아이콘 -->
          <JIcon
            v-if="item.icon"
            :name="item.icon"
            size="sm"
            class="mr-1"
          />
          {{ item.label }}
        </JLink>

        <!-- 구분자 (마지막 아이템이 아니면 표시) -->
        <span
          v-if="!isLastItem(index)"
          :class="preset.separatorClass"
          aria-hidden="true"
        >
          <JIcon
            v-if="separator === 'chevron'"
            name="chevronRight"
            size="sm"
          />
          <span v-else>{{ separator }}</span>
        </span>
      </li>
    </ol>
  </nav>
</template>
